import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, Input, Tag, Row, Modal, Form, Select, Typography, Space, message } from 'antd';
import { SearchOutlined, RightOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import Paginations from '../../components/pagination';
import FilterPopover from '../../components/filter';
import TableData from '../../components/table';
import { categoryActions } from '../../actions/categoryActions';
import { queryActions } from '../../actions/queryActions';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmModal from '../../components/confirmModal';
import QueryDetailPanel from './queryDetailPanel';
import { useCurrentPageAccess } from '../../services/helper';
import SpinnerLoader from '../../components/spinnerLoader';

const { Text } = Typography;

interface QueriesProps {
  role?: string;
}

const Queries: React.FC<QueriesProps> = ({ role }) => {

  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const dispatch = useDispatch();
  const pageAccess = useCurrentPageAccess();
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const categoryData = useSelector((state: any) => state.getCategoryParamsReducer);

  const getQueryState = useSelector((state: any) => state.getQueryReducer);
  const createQueryState = useSelector((state: any) => state.createQueryReducer);
  const deleteQueryState = useSelector((state: any) => state.deleteQueryReducer);
  const viewQueryState = useSelector((state: any) => state.viewQueryReducer);
  const addRemarksState = useSelector((state: any) => state.addRemarksReducer);

  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [visible, setVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<string>('');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const categoryOptions = useMemo(() => {
    const payload = categoryData?.categorydata;
    const list = payload?.categories ?? payload ?? [];
    return (Array.isArray(list) ? list : []).filter((c: any) => c.categoryType === "QUERY").map((c: any) => ({ label: c.categoryName, value: c._id }));
  }, [categoryData]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    if (debouncedSearchText) {
      setCurrentPage(1);
    }
  }, [debouncedSearchText]);

  const fetchList = useCallback((page = 1, size = pageSize, extraParams: any = {}) => {
    setLoading(true);
    const params = {
      pageNumber: page,
      pageSize: size,
      searchText: debouncedSearchText?.trim() || undefined,
      ...appliedFilters,
      ...extraParams
    };
    dispatch(queryActions.listQueries(params));
  }, [appliedFilters, pageSize, debouncedSearchText, dispatch]);

  useEffect(() => {
    dispatch(categoryActions.getCategoryParams({ pageNumber: 1, pageSize: 100 }));
  }, [dispatch]);

  useEffect(() => {
    fetchList(currentPage, pageSize);
  }, [fetchList]);

  // Collapse any open detail panel whenever the result set changes underneath it.
  useEffect(() => {
    setExpandedKey(null);
    setIsEditMode(false);
  }, [currentPage, pageSize, debouncedSearchText, appliedFilters]);

  useEffect(() => {
    if (getQueryState?.isLoading !== undefined) setLoading(!!getQueryState.isLoading);
    if (getQueryState?.isSuccess && getQueryState.querydata) {
      const result = getQueryState.querydata;
      setData(result.data || []);
      setTotal(result.totalRecords || result.total || 0);
      setCurrentPage(result.pageNumber || currentPage);
      setPageSize(result.pageSize || pageSize);
    }
  }, [getQueryState]);

  useEffect(() => {
    if (createQueryState?.isSuccess) {
      setIsCreateOpen(false);
      form.resetFields();
      fetchList(1, pageSize);
    }
  }, [createQueryState]);

  useEffect(() => {
    if (deleteQueryState?.isSuccess) {
      setVisible(false);
      setRecordToDelete('');
      setExpandedKey(null);
      setIsEditMode(false);
      fetchList(currentPage, pageSize);
    }
  }, [deleteQueryState]);

  // Refresh list + open panel after a response is saved.
  useEffect(() => {
    if (addRemarksState?.isSuccess) {
      setIsEditMode(false);
      fetchList(currentPage, pageSize);
      if (expandedKey) dispatch(queryActions.viewQuery(expandedKey));
      addRemarksState.isSuccess = false;
    }
  }, [addRemarksState, expandedKey, currentPage, pageSize, fetchList, dispatch]);

  const handlePageChange = (page: number, newSize?: number) => {
    const size = newSize ?? pageSize;
    setCurrentPage(page);
    setPageSize(size);
    fetchList(page, size);
  };

  const handleApplyFilters = (filters: Record<string, any>) => {
    const applied: any = { ...(filters || {}) };
    if (applied.startDate && applied.startDate.format) applied.startDate = applied.startDate.format('YYYY-MM-DD');
    if (applied.endDate && applied.endDate.format) applied.endDate = applied.endDate.format('YYYY-MM-DD');
    setAppliedFilters(applied);
    setCurrentPage(1);
    fetchList(1, pageSize, applied);
  };

  const handleClearFilters = () => {
    setAppliedFilters({});
    setCurrentPage(1);
    fetchList(1, pageSize, {});
  };

  const handleCreate = async (values: any) => {
    try {
      const payload = {
        categoryId: values.category,
        message: values.message
      };
      dispatch(queryActions.saveQuery(payload));
    } catch (err: any) {
      message.error(err?.message || 'Failed to create query');
    }
  };

  const handleConfirmDelete = () => {
    if (recordToDelete) {
      dispatch(queryActions.deleteQuery(recordToDelete));
    }
  };

  // ── Inline master-detail handlers ─────────────────────────────
  const handleExpand = (expanded: boolean, record: any) => {
    setIsEditMode(false);
    editForm.resetFields();
    if (expanded) {
      setExpandedKey(String(record._id));
      dispatch(queryActions.viewQuery(record._id));
    } else {
      setExpandedKey(null);
    }
  };

  const handlePanelEdit = (detail: any) => {
    editForm.setFieldsValue({
      status: detail?.status,
      remarks: detail?.remarks || '',
    });
    setIsEditMode(true);
  };

  const handlePanelCancel = () => {
    setIsEditMode(false);
    editForm.resetFields();
  };

  const handlePanelSave = (detail: any) => {
    editForm.validateFields().then((values) => {
      if (values.status === 'RESOLVED') {
        if (!values.remarks || values.remarks.trim().length === 0) return;
        dispatch(queryActions.addRemarks({ id: detail?._id, remarks: values.remarks.trim() }));
      }
    });
  };

  const columns = [
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      render: (text: string) => (
        <Space>
          <QuestionCircleOutlined style={{ color: '#4f46e5' }} />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    { title: 'Category', dataIndex: 'categoryName', key: 'categoryName' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => <Tag color={s === 'RESOLVED' ? 'green' : 'gold'}>{s}</Tag>
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: string) => d ? dayjs(d).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Created By',
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (_: any, record: any) => {
        const name = record.createdByName || record.createdBy?.name || '-';
        return <span>{name}</span>;
      }
    }
  ];

  const filterFields = [
    { name: 'status', label: 'Status', type: 'select', options: [{ label: 'All', value: '' }, { label: 'Raised', value: 'RAISED' }, { label: 'Resolved', value: 'RESOLVED' }] },
    { name: 'categoryId', label: 'Category', type: 'select', options: [...categoryOptions] },
    { name: 'startDate', label: 'Start Date', type: 'date', options: [] as any },
    { name: 'endDate', label: 'End Date', type: 'date', options: [] as any }
  ];

  const canRespondRole = (role === 'ADMIN' || role === 'SUPER_ADMIN') && pageAccess.isEdit;
  const canDelete = (role === 'ADMIN' || role === 'SUPER_ADMIN');

  // Merge full detail (from the view endpoint) over the live list row.
  const viewData = (viewQueryState?.isSuccess && String(viewQueryState?.queryview?._id) === String(expandedKey))
    ? viewQueryState.queryview
    : null;
  const detailLoading = Boolean(viewQueryState?.isLoading) && !viewData;

  if (loading) {
    return <SpinnerLoader />;
  }

  const renderExpanded = (record: any) => {
    const detail = viewData ? { ...record, ...viewData } : record;
    return (
      <QueryDetailPanel
        detail={detail}
        loading={detailLoading}
        isEditing={isEditMode}
        canRespond={canRespondRole && detail?.status !== 'RESOLVED'}
        canDelete={canDelete}
        saving={Boolean(addRemarksState?.isLoading)}
        form={editForm}
        onEdit={() => handlePanelEdit(detail)}
        onCancel={handlePanelCancel}
        onSave={() => handlePanelSave(detail)}
        onDelete={() => { setRecordToDelete(detail?._id); setVisible(true); }}
      />
    );
  };

  return (
    <div className="queries-page">
      <Row justify="space-between" align="middle" className="mb-4 category-header">
        <div className="category-toolbar">
          <Input
            placeholder="Search queries"
            prefix={<SearchOutlined />}
            allowClear
            style={{ width: 350 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onPressEnter={() => { setCurrentPage(1); fetchList(1, pageSize); }}
          />
          <FilterPopover
            fields={filterFields}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
            appliedCount={Object.keys(appliedFilters).length}
          />
        </div>
      </Row>
      <div className="driveMynt-card" style={{ marginRight: 20 }}>
        <TableData
          className='query-table query-table--expandable'
          columns={columns as any}
          rowKey={(record: any) => String(record._id)}
          dataSource={data}
          pagination={false}
          expandable={{
            expandedRowKeys: expandedKey ? [expandedKey] : [],
            onExpand: handleExpand,
            expandedRowRender: renderExpanded,
            expandRowByClick: pageAccess.isView,
            rowExpandable: () => pageAccess.isView,
            showExpandColumn: pageAccess.isView,
            columnWidth: 48,
            expandIcon: ({ expanded, onExpand, record }: any) => (
              <span
                className={`cat-expand-ic${expanded ? ' cat-expand-ic--open' : ''}`}
                onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
              >
                <RightOutlined />
              </span>
            ),
          }}
          onRow={(record: any) => ({
            className: String(record._id) === String(expandedKey) ? 'cat-row-active' : '',
            style: { cursor: pageAccess.isView ? 'pointer' : 'default' },
          })}
        />
          <Paginations total={total} defaultCurrent={currentPage} defaultPageSize={pageSize} callback={handlePageChange} />
      </div>
    


      <Modal
        title="Create Query"
        open={isCreateOpen}
        onCancel={() => { setIsCreateOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={categoryOptions} />
          </Form.Item>
          <Form.Item name="message" label="Message" rules={[{ required: true }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModal
        centered
        content="Are you sure you want to delete this query?"
        visible={visible}
        cancelText='cancel'
        okText='ok'
        title='Confirm Delete'
        onOk={handleConfirmDelete}
        onCancel={() => setVisible(false)}
      />
    </div>
  );
};

export default Queries;
